import type {
  CurrentWhitelabelConfigResponse,
  ResolveWhitelabelConfigResponse,
} from "@nlc/shared-types";

import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { unauthenticatedApi } from "@/lib/api/client";
import {
  BRAND_SHADES,
  generateBrandPalette,
} from "@/lib/whitelabel/brand-palette";
import { isKnownNlcDomain } from "@/lib/whitelabel/domains";

// ─── Types ────────────────────────────────────────────────────────────────────

interface WhitelabelContextValue {
  /** The resolved config data (partner branding) */
  config: null | ResolveWhitelabelConfigResponse;
  /** Whether a whitelabel config has been detected (domain or project) */
  isWhitelabel: boolean;
  /** Whether the initial domain resolution is still loading */
  loading: boolean;
  /** Update config from authenticated endpoint after login */
  setAuthenticatedConfig: (data: CurrentWhitelabelConfigResponse) => void;
}

// ─── Context ──────────────────────────────────────────────────────────────────

// ─── Context (exported for hook file) ─────────────────────────────────────────

export const WhitelabelContext = createContext<WhitelabelContextValue>({
  config: null,
  isWhitelabel: false,
  loading: true,
  setAuthenticatedConfig: () => {},
});

// ─── Dynamic theming ──────────────────────────────────────────────────────────

const DEFAULT_MANIFEST_HREF = "/site.webmanifest";

let generatedManifestUrl: null | string = null;

function setAppleTouchIcon(href: string) {
  for (const el of document.querySelectorAll("link[rel='apple-touch-icon']")) {
    el.remove();
  }
  const link = document.createElement("link");

  link.rel = "apple-touch-icon";
  link.href = href;
  document.head.append(link);
}

function setFavicon(icons: { href: string; sizes?: string; type: string }[]) {
  for (const el of document.querySelectorAll("link[rel~='icon']")) {
    el.remove();
  }

  for (const icon of icons) {
    const link = document.createElement("link");

    link.rel = "icon";
    link.type = icon.type;
    if (icon.sizes) {
      link.setAttribute("sizes", icon.sizes);
    }
    link.href = icon.href;
    document.head.append(link);
  }
}

// index.html ships with no favicon tags — set the NLC defaults here so
// there's still a fallback for non-whitelabel domains.
function setDefaultFavicon() {
  setFavicon([
    { href: "/favicon.svg", type: "image/svg+xml" },
    { href: "/favicon-32x32.png", sizes: "32x32", type: "image/png" },
    { href: "/favicon-16x16.png", sizes: "16x16", type: "image/png" },
  ]);
  setAppleTouchIcon("/apple-touch-icon.png");
}

function setManifestHref(href: string) {
  let link = document.querySelector<HTMLLinkElement>("link[rel='manifest']");

  if (!link) {
    link = document.createElement("link");
    link.rel = "manifest";
    document.head.append(link);
  }
  link.href = href;
}

function applyWhitelabelManifest(faviconUrl: string, partnerName: string) {
  const manifest = {
    background_color: "#ffffff",
    display: "standalone",
    icons: [
      { sizes: "192x192", src: faviconUrl, type: "image/png" },
      { sizes: "512x512", src: faviconUrl, type: "image/png" },
    ],
    name: partnerName,
    short_name: partnerName,
    theme_color: "#ffffff",
  };

  if (generatedManifestUrl) {
    URL.revokeObjectURL(generatedManifestUrl);
  }

  const blob = new Blob([JSON.stringify(manifest)], {
    type: "application/manifest+json",
  });

  generatedManifestUrl = URL.createObjectURL(blob);
  setManifestHref(generatedManifestUrl);
}

function clearWhitelabelManifest() {
  if (generatedManifestUrl) {
    URL.revokeObjectURL(generatedManifestUrl);
    generatedManifestUrl = null;
  }
  setManifestHref(DEFAULT_MANIFEST_HREF);
}

function applyWhitelabelTheme({
  faviconUrl,
  partnerName,
  primaryColor,
}: {
  faviconUrl: null | string;
  partnerName: string;
  primaryColor: null | string;
}) {
  const root = document.documentElement;

  if (primaryColor) {
    const palette = generateBrandPalette(primaryColor);

    if (palette) {
      for (const shade of BRAND_SHADES) {
        root.style.setProperty(`--brand-${shade}`, palette[shade] ?? null);
      }
    }
  }

  if (faviconUrl) {
    setFavicon([{ href: faviconUrl, type: "image/png" }]);
    setAppleTouchIcon(faviconUrl);
    applyWhitelabelManifest(faviconUrl, partnerName);
  }

  if (partnerName) {
    document.title = partnerName;
  }

  localStorage.setItem("partnerName", partnerName);
  localStorage.setItem("primaryColor", primaryColor!);
  localStorage.setItem("faviconUrl", faviconUrl ?? "");
}

function clearWhitelabelTheme() {
  const root = document.documentElement;

  for (const shade of BRAND_SHADES) {
    root.style.removeProperty(`--brand-${shade}`);
  }

  setDefaultFavicon();
  clearWhitelabelManifest();
}

// ─── Provider ─────────────────────────────────────────────────────────────────

interface WhitelabelProviderProps {
  children: React.ReactNode;
}

export function WhitelabelProvider({ children }: WhitelabelProviderProps) {
  const [config, setConfig] = useState<null | ResolveWhitelabelConfigResponse>(
    null,
  );
  const [isWhitelabel, setIsWhitelabel] = useState(false);
  const [loading, setLoading] = useState(true);

  // Pre-login: resolve by current domain
  useEffect(() => {
    const domain = window.location.hostname;

    // Skip resolution for known NLC domains
    if (isKnownNlcDomain(domain)) {
      setDefaultFavicon();
      setLoading(false);

      return;
    }

    unauthenticatedApi
      .url("/whitelabel/resolve")
      .query({ domain })
      .get()
      .json<ResolveWhitelabelConfigResponse>()
      .then((data) => {
        setConfig(data);
        setIsWhitelabel(true);
        applyWhitelabelTheme({
          faviconUrl: data.faviconUrl,
          partnerName: data.partnerName,
          primaryColor: data.primaryColor,
        });
      })
      .catch(() => {
        // Not a whitelabel domain — that's fine
        setDefaultFavicon();
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // Post-login: update with authenticated config
  // Theme is determined exclusively by domain resolution (pre-login).
  // If the domain resolved a whitelabel config, keep it for all users on this domain.

  // If not, clear any theme that may have persisted from a previous session.
  const setAuthenticatedConfig = useCallback(
    (_data: CurrentWhitelabelConfigResponse) => {
      if (config) {
        setIsWhitelabel(true);

        return;
      }

      setIsWhitelabel(false);
      setConfig(null);
      clearWhitelabelTheme();

      localStorage.removeItem("partnerName");
      localStorage.removeItem("primaryColor");
      localStorage.removeItem("faviconUrl");
    },
    [config],
  );

  const value = useMemo<WhitelabelContextValue>(
    () => ({
      config,
      isWhitelabel,
      loading,
      setAuthenticatedConfig,
    }),
    [config, isWhitelabel, loading, setAuthenticatedConfig],
  );

  return (
    <WhitelabelContext.Provider value={value}>
      {children}
    </WhitelabelContext.Provider>
  );
}
